import React from "react";
import { Download, MessageSquareReply, Paperclip } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");
const humanize = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const STATUS_PILL = {
  SUBMITTED: "bg-purple-100 text-purple-700",
  CHANGES_REQUESTED: "bg-orange-100 text-orange-700",
  ACCEPTED: "bg-emerald-100 text-emerald-700",
};

function Bubble({ side, children }) {
  return (
    <div className={`flex ${side === "right" ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%]">{children}</div>
    </div>
  );
}

/**
 * Threaded view of the deliverable review loop: each freelancer submission (files + note) followed by
 * the architect's change-request feedback, in chronological order.
 *
 * Props: deliverables[], perspective ("ARCHITECT" | "FREELANCER"), onOpen(s3Key).
 */
function DeliverableThread({ deliverables = [], perspective = "ARCHITECT", onOpen }) {
  if (!deliverables.length) {
    return <p className="text-sm text-gray-400">No submissions yet.</p>;
  }
  const flSide = perspective === "FREELANCER" ? "right" : "left";
  const arSide = perspective === "ARCHITECT" ? "right" : "left";
  const flAuthor = perspective === "FREELANCER" ? "You" : "Freelancer";
  const arAuthor = perspective === "ARCHITECT" ? "You" : "Client";

  return (
    <div className="space-y-3">
      {deliverables.map((d) => (
        <React.Fragment key={d.deliverableId}>
          {/* Freelancer submission */}
          <Bubble side={flSide}>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-500">
                  {flAuthor} · Version {d.version}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_PILL[d.status] || "bg-gray-100 text-gray-600"}`}>
                  {humanize(d.status)}
                </span>
              </div>
              {d.note && <p className="text-sm text-gray-700">{d.note}</p>}
              {(d.s3Keys || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {(d.s3Keys || []).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => onOpen?.(k)}
                      className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200"
                    >
                      <Download size={12} /> {k.split("/").pop()}
                    </button>
                  ))}
                </div>
              )}
              {!(d.s3Keys || []).length && (
                <p className="mt-1 inline-flex items-center gap-1 text-xs text-gray-400">
                  <Paperclip size={11} /> no files
                </p>
              )}
              <div className="mt-1 text-[11px] text-gray-400">{fmtDate(d.createdDate)}</div>
            </div>
          </Bubble>

          {/* Architect change-request response (only when changes were requested) */}
          {d.architectFeedback && (
            <Bubble side={arSide}>
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-orange-700">
                  <MessageSquareReply size={13} /> {arAuthor} requested changes
                </div>
                <p className="text-sm text-orange-800">{d.architectFeedback}</p>
              </div>
            </Bubble>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default DeliverableThread;
